import type { datadogLogs } from '@datadog/browser-logs';
import type { datadogRum } from '@datadog/browser-rum';

/**
 * Lazy Datadog SDKs (round 3 lane 8, H2-312). RUM and Browser Logs together are the heaviest
 * thing the portal ships (~180 KiB gzip of modules), and nothing may initialise them until the
 * visitor accepts the `analytics` cookie category (H2-204). So neither belongs in the entry chunk:
 * `DatadogRUM` calls {@link loadDatadog} on the first consent with Datadog enabled, and everything
 * else that talks to the SDKs — the logger, the view tracker, product actions — reads
 * {@link loadedDatadog} or waits on {@link onDatadogLoaded}. With Datadog off at build time, or no
 * consent, the SDK chunk is never fetched.
 */

export type DatadogSdk = { datadogRum: typeof datadogRum; datadogLogs: typeof datadogLogs };

let loaded: DatadogSdk | null = null;
let loading: Promise<DatadogSdk> | null = null;
const listeners = new Set<(sdk: DatadogSdk) => void>();

/** @desc The SDKs if they have loaded, else `null` (Datadog off, no consent yet, or loading). */
export const loadedDatadog = (): DatadogSdk | null => loaded;

/**
 * @desc Runs `listener` with the SDKs as soon as they have loaded — at once if they already have.
 * Returns an unsubscribe for a listener that has not fired yet.
 */
export function onDatadogLoaded(listener: (sdk: DatadogSdk) => void): () => void {
  if (loaded) {
    listener(loaded);
    return () => undefined;
  }
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * @desc Fetches both SDKs (one request per chunk, shared by every caller). Listeners run before the
 * returned promise resolves, so anything they queue reaches the SDK ahead of `init`, which the SDK
 * buffers exactly as it buffered pre-`init` calls when it was bundled. A failed load is not cached:
 * the next call retries.
 */
export function loadDatadog(): Promise<DatadogSdk> {
  loading ??= Promise.all([import('@datadog/browser-rum'), import('@datadog/browser-logs')])
    .then(([rum, logs]) => {
      const sdk = { datadogRum: rum.datadogRum, datadogLogs: logs.datadogLogs };
      loaded = sdk;
      for (const listener of listeners) listener(sdk);
      listeners.clear();
      return sdk;
    })
    .catch((err: unknown) => {
      loading = null;
      throw err;
    });
  return loading;
}

/** @desc Test-only: forget the loaded SDKs and any listeners. */
export function resetDatadogForTests(): void {
  loaded = null;
  loading = null;
  listeners.clear();
}
